import {
  SmartRateCellVariant,
  smartRateCellVariantFromJSON,
} from '@classpass/protobuf-definitions/lib/web/models/partners/class_utilization_insights';

import { HEATMAP_DAYS } from 'constants/heatmap';
import { formatHeatmapHourLabel } from 'helpers/heatmap';
import { EARNINGS_BINS } from './WeeklyEarnings.constants';

export { HEATMAP_DAYS as DAYS };

export const getEarningsColor = value => {
  const numeric = Number(value);
  const bin = !Number.isFinite(numeric)
    ? EARNINGS_BINS[0]
    : EARNINGS_BINS.find(b => numeric <= b.max) || EARNINGS_BINS[EARNINGS_BINS.length - 1];
  return { background: bin.background, text: bin.text };
};

export const sortClassesByName = (classes = []) =>
  [...classes].sort((a, b) => (a.className || '').localeCompare(b.className || ''));

export const CHART_METRIC = Object.freeze({
  TOTAL_EARNINGS: 'TOTAL_EARNINGS',
  AVERAGE_PAYOUT_RATE: 'AVERAGE_PAYOUT_RATE',
});

export const VENUE_NOT_ON_SMART_RATE_MESSAGE = 'Venue is not on SmartRate';

export const CELL_STATE = Object.freeze({
  EARNINGS: 'EARNINGS',
  NO_SPOTS: 'NO_SPOTS',
  NO_CP_BOOKINGS: 'NO_CP_BOOKINGS',
  EMPTY: 'EMPTY',
});

export const buildEarningsFormatter = (currencyCode = 'USD', { compact = false } = {}) => {
  const fullFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 0,
  });
  if (!compact) return fullFormatter;
  const compactFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 1,
    notation: 'compact',
  });
  return {
    format: value => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return fullFormatter.format(0);
      if (numeric <= 999) return fullFormatter.format(numeric);
      return compactFormatter.format(numeric);
    },
  };
};

const orderedRowsByHour = rowsByHour =>
  Array.from(rowsByHour.values()).sort((a, b) => {
    const aHour = Number(a.hour);
    const bHour = Number(b.hour);
    if (Number.isFinite(aHour) && Number.isFinite(bHour)) return aHour - bHour;
    return a.time.localeCompare(b.time);
  });

const getCellForDatapoint = datapoint => {
  const base = {
    classpassBooked: datapoint.classpass_booked ?? null,
    excessSpotsFilledPercent: datapoint.excess_spots_filled_percent ?? null,
    day: datapoint.day ?? null,
    time: datapoint.time ?? null,
  };
  switch (smartRateCellVariantFromJSON(datapoint.smartrate_cell_variant)) {
    case SmartRateCellVariant.HAS_DATA:
      return { ...base, state: CELL_STATE.EARNINGS, value: datapoint.value };
    case SmartRateCellVariant.NO_CP_SPOTS:
      return { ...base, state: CELL_STATE.NO_SPOTS };
    case SmartRateCellVariant.NO_CP_BOOKINGS:
      return { ...base, state: CELL_STATE.NO_CP_BOOKINGS };
    case SmartRateCellVariant.NO_SCHEDULES:
    default:
      return { state: CELL_STATE.EMPTY };
  }
};

export const getClassStorageKey = venueId => `weekly-earnings-class-${venueId}`;

export const readPersistedClassId = venueId => {
  try {
    return sessionStorage.getItem(getClassStorageKey(venueId)) || null;
  } catch {
    return null;
  }
};

export const persistClassId = (venueId, classId) => {
  try {
    if (classId != null) {
      sessionStorage.setItem(getClassStorageKey(venueId), String(classId));
    } else {
      sessionStorage.removeItem(getClassStorageKey(venueId));
    }
  } catch {
    // sessionStorage unavailable
  }
};

export const isVenueNotOnSmartRateError = error =>
  error?.response?.status === 400 &&
  error?.response?.data?.code === 400 &&
  error?.response?.data?.message === VENUE_NOT_ON_SMART_RATE_MESSAGE;

export const adaptWeeklyEarningsResponse = response => {
  const datapoints = (response?.data?.visualization?.heatmap?.data ?? [])
    .map(datapoint => datapoint.smartrate_datapoint)
    .filter(Boolean);

  const rowsByHour = new Map();
  datapoints.forEach(datapoint => {
    const hour = datapoint.column;
    if (!rowsByHour.has(hour)) {
      rowsByHour.set(hour, {
        hour,
        time: formatHeatmapHourLabel(datapoint.time),
        cells: HEATMAP_DAYS.map(() => ({ state: CELL_STATE.EMPTY })),
      });
    }

    // `row` is the day-of-week index (0 = Sunday); proto3 omits the default 0.
    const dayIndex = datapoint.row ?? 0;
    if (dayIndex >= 0 && dayIndex < HEATMAP_DAYS.length) {
      rowsByHour.get(hour).cells[dayIndex] = getCellForDatapoint(datapoint);
    }
  });

  return {
    currency: response?.data?.currency || 'USD',
    hasMoreThan8WeeksData: response?.has_more_than_8_weeks_data !== false,
    hasQualifyingReservationCount: response?.has_qualifying_reservation_count !== false,
    lastUpdatedOn: response?.last_updated_on,
    mustSelectClassType: !!response?.must_select_class_type,
    rows: orderedRowsByHour(rowsByHour),
    upcomingOpportunitiesCount: 0,
  };
};

export const createComingSoonWeeklyEarningsData = () =>
  adaptWeeklyEarningsResponse({
    has_more_than_8_weeks_data: false,
    has_qualifying_reservation_count: true,
    must_select_class_type: false,
  });
